import type { ComponentType, LazyExoticComponent } from "react";

export type FeatureNavigationMeta = {
  label: string;
  path: string;
  icon: ComponentType<{ className?: string }>;
  order: number;
  parentId?: string;
};

export type FeatureCrudPermissions = {
  view: string;
  /** When set, gates the route/nav on holding ANY of these instead of the single `view` permission. */
  viewAny?: string[];
  viewPrevious?: string;
  create: string;
  edit: string;
  delete: string;
  submitOnline?: string;
  onlineApplication?: string;
  approveApplication?: string;
};

export type FeatureRouteMeta = {
  path: string;
  element: LazyExoticComponent<ComponentType<any>>;
};

export type GuardianNavigationMeta = {
  label?: string;
  order?: number;
  permission?: string;
};

export type NavigationPersona = "staff" | "guardian";

/** OR semantics: true when `permission` is unset, or `can` returns true for at least one entry. */
export function canAny(
  can: (permission?: string) => boolean,
  permission?: string | string[],
): boolean {
  if (!permission) return true;
  return Array.isArray(permission) ? permission.some((p) => can(p)) : can(permission);
}

export function getFeatureNavigationPermission(
  feature: FeatureMetadata,
  persona: NavigationPersona,
): string | string[] | undefined {
  if (persona === "guardian") {
    return feature.guardianNavigation?.permission ?? feature.permissions?.view;
  }
  return feature.permissions?.viewAny ?? feature.permissions?.view;
}

export type FeatureMetadata = {
  id: string;
  moduleName: string;
  navigation: FeatureNavigationMeta;
  guardianNavigation?: GuardianNavigationMeta;
  permissions: FeatureCrudPermissions;
  route: FeatureRouteMeta;
  readiness: "draft" | "alpha" | "beta" | "live";
};

/**
 * A tab contributed to the Administration → Settings page. Each owning module exports its
 * own tab config (e.g. src/features/branding/sidebar/sidebarBranding.settingsTab.ts) — the
 * Settings page itself only knows this shape, never another module's file layout. Mirrors how
 * FeatureMetadata lets src/features/featureRegistry.ts compose every module's routes without
 * modules importing each other directly.
 */
export type SettingsTabConfig = {
  id: string;
  label: string;
  /** Permission required to see this tab. Omit for tabs with no additional gating. */
  permission?: string;
  loader: () => Promise<{ default: ComponentType<Record<string, never>> }>;
};
